// The to-do cards at the top of Home and the Dashboard, by role:
//   admin       -> "Unassigned": new leads with no counsellor, each with a dropdown to hand it to one
//   counsellor  -> "Needs a telecaller": students they own that no telecaller has been given, each with a dropdown of THEIR team
//   telecaller  -> nothing here: their whole home is the call queue
// Choosing a name saves at once. The row disappears from the card because it is no longer unassigned.
import { useState } from 'react';

import { InlineSelect, StatusChip } from 'src/front-components/lead-desk/kit';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { ShellCard, ShellEmptyState, ShellSectionLabel } from 'src/front-components/theme/page-chrome';
import { ErrorLine } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { distributeLeads, updateStudent } from 'src/front-components/utils/lead-desk-data';
import { DeskButton } from 'src/front-components/theme/desk-controls';
import { assignmentValue, DESK_STATUS_LABELS, needsTelecaller, SOURCE_LABELS, studentPhone, telecallerChoices, unassignedStudents, UNASSIGNED, type StudentRow } from 'src/utils/lead-desk';

const SHOW = 8;

export const WorkInbox = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const [failure, setFailure] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  if (ctx.role === 'TELECALLER') return null;

  const isAdmin = ctx.role === 'ADMIN' || ctx.role === 'SUPER_ADMIN' || ctx.role === 'TENANT_ADMIN';
  const isCounsellor = ctx.role === 'COUNSELLOR';
  const myTelecallers = telecallerChoices(ctx.people, ctx.person);
  const rows: StudentRow[] = isAdmin ? unassignedStudents(ctx.students) : needsTelecaller(ctx.students);
  const canDistribute = isAdmin ? rows.length > 0 : isCounsellor && rows.length > 0 && myTelecallers.length > 0;
  const shown = all ? rows : rows.slice(0, SHOW);

  const assign = async (student: StudentRow, key: 'counsellorName' | 'telecallerName', next: string): Promise<void> => {
    setFailure(null);
    await updateStudent(student.id, { [key]: assignmentValue(next) });
    await ctx.refresh();
  };

  // Admin: no hand-picking. One press gives every waiting lead to the next counsellor and that counsellor's next telecaller, in turn.
  const distribute = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    setResult(null);

    try {
      const out = await distributeLeads();
      const split = Object.entries(out.given).map(([name, n]) => `${name} ${n}`).join(', ');

      await ctx.refresh();
      setResult(`${out.assigned} lead${out.assigned === 1 ? '' : 's'} assigned: ${split}.${out.withoutTelecaller > 0 ? ` ${out.withoutTelecaller} went to a counsellor with no telecaller yet.` : ''}`);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not give the leads out.');
    } finally {
      setBusy(false);
    }
  };

  const options = (student: StudentRow): Array<{ value: string; label: string }> => [
    { value: UNASSIGNED, label: '-- Choose telecaller --' },
    ...telecallerChoices(ctx.people, student.counsellorName).map((t) => ({ value: t, label: t })),
  ];

  const title = isAdmin ? 'Unassigned leads' : 'Needs a telecaller';
  const hint = isAdmin ? 'New leads are given out in turn: the next counselor, then that counselor\'s next telecaller. New students and converted enquiries are handed out automatically; these are ones still waiting.' : 'Students you own that no telecaller has been given. Hand each to one of your telecallers.';

  return (
    <ShellCard scheme={ctx.scheme}>
      <ShellSectionLabel
        scheme={ctx.scheme}
        action={
          canDistribute ? (
            <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void distribute()}>
              {busy ? 'Distributing…' : isAdmin ? `Distribute ${rows.length} now` : `Distribute ${rows.length} to Telecallers`}
            </DeskButton>
          ) : rows.length > 0 ? (
            <StatusChip label={`${rows.length} waiting`} color="orange" />
          ) : undefined
        }
      >
        {title}
      </ShellSectionLabel>
      <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[3] }}>{hint}</div>
      <ErrorLine scheme={ctx.scheme} message={failure} />
      {result !== null && <div style={{ ...SHELL_TYPE.body, color: shell.text, marginBottom: SHELL_SPACE[3] }}>{result}</div>}

      {rows.length === 0 ? (
        <ShellEmptyState scheme={ctx.scheme} message={isAdmin ? 'Every lead has a counselor.' : 'Every student of yours has a telecaller.'} />
      ) : !isAdmin && telecallerChoices(ctx.people, ctx.person).length === 0 ? (
        <ShellEmptyState scheme={ctx.scheme} message="You have no telecallers yet." hint="Ask the admin to add telecallers under you in Manage Users." />
      ) : (
        <>
          {shown.map((student) => (
            <div key={student.id} style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[3], padding: `${SHELL_SPACE[2]} 0`, borderTop: `1px solid ${shell.borderLight}`, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text }}>{student.name ?? '—'}</div>
                <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>
                  {studentPhone(student)} · {SOURCE_LABELS[student.leadSource ?? ''] ?? 'Unknown source'} · {DESK_STATUS_LABELS[student.deskStatus ?? ''] ?? '—'}
                </div>
              </div>
              {!isAdmin && (
                <InlineSelect
                  scheme={ctx.scheme}
                  label={`Telecaller for ${student.name ?? 'student'}`}
                  value={UNASSIGNED}
                  options={options(student)}
                  onSave={(next) => (next === UNASSIGNED ? Promise.resolve() : assign(student, 'telecallerName', next))}
                  onError={setFailure}
                  width="190px"
                />
              )}
            </div>
          ))}
          {rows.length > SHOW && (
            <button type="button" onClick={() => setAll(!all)} style={{ marginTop: SHELL_SPACE[2], border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--t-color-blue)', ...SHELL_TYPE.body }}>
              {all ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </ShellCard>
  );
};
